/**
 * Pre-validation of platform (Keycloak) login passwords.
 *
 * Mirrors the equivalent password-policy helper that lives in the backend
 * repository (tessera-labs-inc/backend, src/utils/password_policy.py), which in
 * turn mirrors the Keycloak realm password policy. This lets forms surface
 * clear, instant feedback before a request reaches the API/Keycloak. Keycloak
 * remains the source of truth; if the realm policy changes, update this file,
 * the backend helper, and the Keycloak realm config to match.
 *
 * Current policy enforced here: length >= 8, >= 1 uppercase, >= 1 lowercase,
 * >= 1 digit, >= 1 special (non-alphanumeric) character.
 *
 * This module is pure (no i18n): it returns stable violation *codes* so callers
 * can localize them. See `usePasswordPolicy` for localized messages.
 */

export const PASSWORD_MIN_LENGTH = 8;

/** Stable codes for each policy rule, in display order. */
export type PasswordPolicyViolation = 'minLength' | 'uppercase' | 'lowercase' | 'number' | 'special';

/**
 * Return the codes for every policy rule the given password fails.
 *
 * An empty array means the password satisfies the policy. A "special"
 * character is any character that is neither a letter nor a digit, matching
 * Keycloak's `specialChars` definition.
 *
 * @param password - The candidate password to check.
 * @returns Violation codes, one per unmet requirement (empty if valid).
 * @example
 * validatePassword('New-Pass1'); // []
 * validatePassword('weak'); // ['minLength', 'uppercase', 'number', 'special']
 */
export function validatePassword(password: string): PasswordPolicyViolation[] {
  const violations: PasswordPolicyViolation[] = [];

  if (password.length < PASSWORD_MIN_LENGTH) {
    violations.push('minLength');
  }
  if (!/[A-Z]/.test(password)) {
    violations.push('uppercase');
  }
  if (!/[a-z]/.test(password)) {
    violations.push('lowercase');
  }
  if (!/[0-9]/.test(password)) {
    violations.push('number');
  }
  if (!/[^a-zA-Z0-9]/.test(password)) {
    violations.push('special');
  }

  return violations;
}
